import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMessage } from "../../lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const RecapControls = () => {
  const [enabled, setEnabled] = useState(null);
  const [day, setDay] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/recap/settings")
      .then(({ data }) => { setEnabled(data.enabled); setDay(data.day ?? null); })
      .catch(() => setEnabled(false));
  }, []);

  const save = async (nextEnabled, nextDay) => {
    const prev = { enabled, day };
    setEnabled(nextEnabled);
    setDay(nextDay);
    try {
      await api.put("/recap/settings", { enabled: nextEnabled, day: nextDay });
      toast.success(nextEnabled
        ? `Weekly email recap on${nextDay !== null ? ` — arrives ${DAYS[nextDay]}s` : ""}`
        : "Weekly email recap turned off");
    } catch (e) {
      setEnabled(prev.enabled);
      setDay(prev.day);
      toast.error(errMessage(e));
    }
  };
  const toggle = (value) => save(value, day);

  // Digest Preview: fetch the exact HTML the recap email would contain and
  // show it in a sandboxed iframe — what you see is what will send.
  const [preview, setPreview] = useState(null); // null | "loading" | html
  const openPreview = async () => {
    setPreview("loading");
    try {
      const { data } = await api.get("/recap/digest-preview");
      setPreview(data.html);
    } catch (e) {
      setPreview(null);
      toast.error(errMessage(e));
    }
  };

  const sendNow = async () => {
    setSending(true);
    // Immediate feedback: upstream email sends can take >10s when the
    // provider retries, so show a loading toast that resolves in place.
    const tid = toast.loading("Sending your recap…");
    try {
      await api.post("/recap/send-now");
      toast.success("Recap email sent — check your inbox!", { id: tid });
    } catch (e) {
      toast.error(errMessage(e), { id: tid });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 flex flex-wrap items-center justify-between gap-4" data-testid="recap-controls">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-secondary" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">Weekly email recap</p>
          <p className="text-xs text-muted-foreground">A friendly weekly summary of what your children practiced, sent to your email.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {enabled === null ? (
          <span className="text-xs text-muted-foreground" data-testid="recap-loading">Loading…</span>
        ) : (
          <>
            <select value={day === null ? "any" : String(day)} disabled={!enabled}
              onChange={(e) => save(enabled, e.target.value === "any" ? null : Number(e.target.value))}
              className="h-8 rounded-lg border border-border bg-card px-2 text-xs disabled:opacity-50"
              aria-label="Recap email day" data-testid="recap-day-select">
              <option value="any">Any day</option>
              {DAYS.map((d, i) => <option key={d} value={i}>{`${d}s`}</option>)}
            </select>
            <Switch checked={Boolean(enabled)} onCheckedChange={(v) => toggle(Boolean(v))}
              aria-label="Weekly email recap" data-testid="recap-toggle" />
          </>
        )}
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" disabled={enabled === null || preview === "loading"} onClick={openPreview} data-testid="recap-digest-preview-button">
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
          {preview === "loading" ? "Building…" : "Preview"}
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" disabled={sending || enabled === null} onClick={sendNow} data-testid="recap-send-now-button">
          {sending ? "Sending…" : "Send now"}
        </Button>
      </div>
      <Dialog open={preview !== null && preview !== "loading"} onOpenChange={(o) => !o && setPreview(null)}>
        {/* Keep initial focus OUT of the iframe (it swallows keydown, which
            would stop Escape from closing the dialog). */}
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="recap-preview-modal"
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Mail className="h-5 w-5 text-secondary" aria-hidden="true" /> This week&apos;s recap — live preview
            </DialogTitle>
          </DialogHeader>
          <iframe title="Weekly recap preview" srcDoc={typeof preview === "string" ? preview : ""}
            sandbox="" tabIndex={-1}
            className="w-full flex-1 min-h-[60vh] rounded-lg border border-border bg-white"
            data-testid="recap-preview-frame" />
          <p className="text-[11px] text-muted-foreground">Exactly what will land in your inbox — links are disabled in the preview.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};
